import type { ComponentType } from 'react';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { isLowerEnv } from '@/config/utils';
import { AppSubscriber } from '@/containers/Subscriber/AppSubscriber';
import { RootVendors } from '@/containers/Subscriber/RootVendors';
import { Layout } from '@/Layout';
import { ProvideDefaultPaths } from '@/router/ProvideDefaultPaths';
import { Home } from '@/screens/Home/Home';
import { IFrameTest } from '@/screens/IFrameTest/IFrameTest';
import { OAuth2, OAuth2Logout, SalesforceAuthCallback } from '@/screens/Inbound';
import { OAuth2Login } from '@/screens/Inbound/Auth.OAuth2.Login';
import { TrinityEdgeAuthCallback } from '@/screens/Inbound/Auth.TrinityEdge';
import { LoginScreen } from '@/screens/Login';
import { LogoutScreen } from '@/screens/Logout';
import { UserDisabled } from '@/screens/UserDisabled/UserDisabled';
import { AppInitializedGuard } from './Guard.App';
import { AuthenticatedGuard, UnauthenticatedGuard } from './Guard.Authentication';
import { BrandingInitializedGuard } from './Guard.Branding';
import { RedirectToAllTab, RedirectToFilePreview } from './Redirects';
import { getAppDefaultPaths } from './utils';

/**
 * Wrapper around `React.lazy` that auto-reloads the page when a dynamic chunk
 * fails to load (e.g. after a redeployment replaced the hashed chunk files).
 */
function lazyWithReload<T extends { default: ComponentType }>(
  importFn: () => Promise<T>
): React.LazyExoticComponent<T['default']> {
  return lazy(() =>
    importFn().catch(() => {
      window.location.reload();
      // Return a never-resolving promise so React keeps the fallback
      // until the browser reload takes over.
      return new Promise<T>(() => {});
    })
  );
}

// --- Lazy screen imports ---

const LazyCalendar = lazyWithReload(() =>
  import('@/screens/Calendar').then((m) => ({ default: m.Calendar }))
);

const LazyChatScreen = lazyWithReload(() =>
  import('@/screens/Chat').then((m) => ({ default: m.ChatScreen }))
);

const LazyDiagnosticsScreen = lazyWithReload(() =>
  import('@/screens/Diagnostics').then((m) => ({ default: m.DiagnosticsScreen }))
);

const LazyAnalystMain = lazyWithReload(() =>
  import('@/screens/GroupMain').then((m) => ({ default: m.AnalystMain }))
);

const LazyPageScreen = lazyWithReload(() =>
  import('@/screens/Page/Page').then((m) => ({ default: m.PageScreen }))
);

// Project screens
const LazyProjectMain = lazyWithReload(() =>
  import('@/screens/Project.Main').then((m) => ({ default: m.ProjectMain }))
);

const LazyProjectChartLibrary = lazyWithReload(() =>
  import('@/screens/Project.ChartLibrary').then((m) => ({ default: m.ProjectChartLibrary }))
);

const LazyProjectDashboard = lazyWithReload(() =>
  import('@/screens/Project.Dashboard/Project.Dashboard').then((m) => ({ default: m.ProjectDashboard }))
);

const LazyProjectDetails = lazyWithReload(() =>
  import('@/screens/Project.Details').then((m) => ({ default: m.ProjectDetails }))
);

const LazyProjectEntities = lazyWithReload(() =>
  import('@/screens/Project.Entities').then((m) => ({ default: m.ProjectEntities }))
);

const LazyProjectFiles = lazyWithReload(() =>
  import('@/screens/Project.Files').then((m) => ({ default: m.ProjectFiles }))
);

const LazyProjectHighlights = lazyWithReload(() =>
  import('@/screens/Project.Highlights').then((m) => ({ default: m.ProjectHighlights }))
);

const LazyProjectSummary = lazyWithReload(() =>
  import('@/screens/Project.Summary').then((m) => ({ default: m.ProjectSummary }))
);

const LazyProjectTags = lazyWithReload(() =>
  import('@/screens/Project.Tags').then((m) => ({ default: m.ProjectTags }))
);

const LazyProjectPatientJourney = lazyWithReload(() =>
  import('@/screens/Project.PatientJourney').then((m) => ({ default: m.ProjectPatientJourney }))
);

const LazyPatientJourneyStandalonePage = lazyWithReload(() =>
  import('@/screens/Project.PatientJourney').then((m) => ({ default: m.PatientJourneyStandalonePage }))
);

const LazyProjectPatientJourneyDashboardContainer = lazyWithReload(() =>
  import('@/screens/Project.Main/Project.PatientJourney.Container').then((m) => ({
    default: m.ProjectPatientJourneyDashboardContainer,
  }))
);

const LazyProjectPatientJourneyStageContainer = lazyWithReload(() =>
  import('@/screens/Project.Main/Project.PatientJourney.Container').then((m) => ({
    default: m.ProjectPatientJourneyStageContainer,
  }))
);

const LazyProjectPatientJourneyManageContainer = lazyWithReload(() =>
  import('@/screens/Project.Main/Project.PatientJourney.Container').then((m) => ({
    default: m.ProjectPatientJourneyManageContainer,
  }))
);

const LazyProjectResponses = lazyWithReload(() =>
  import('@/screens/Project.Responses/Project.Responses').then((m) => ({ default: m.ProjectResponses }))
);

const LazyQuantAnalysis = lazyWithReload(() =>
  import('@/screens/Project.QuantAnalysis').then((m) => ({ default: m.QuantAnalysis }))
);

// Plain "Project Segments" CRUD tab, gated by ProjectSubtype.Segmentation (see
// Project.Tabs.Container.tsx). Replaces the previous hardcoded-project-id wiring to the
// segments_v2-backed screens/Segments/* — those screens and their own /segments-flat backend
// stay in the repo, just unreferenced here.
const LazyProjectSegmentsList = lazyWithReload(() =>
  import('@/screens/Project.Segments').then((m) => ({ default: m.ProjectSegmentsList }))
);

const LazyProjectSegmentProfile = lazyWithReload(() =>
  import('@/screens/Project.Segments').then((m) => ({ default: m.ProjectSegmentProfile }))
);

const LazyProjectSegmentDashboard = lazyWithReload(() =>
  import('@/screens/Project.Segments').then((m) => ({ default: m.ProjectSegmentDashboard }))
);

const LazyProjectHCPDigitalTwins = lazyWithReload(() =>
  import('@/screens/Project.Segments').then((m) => ({ default: m.ProjectHCPDigitalTwins }))
);

// Workspace screens
const LazyWorkspaceLayout = lazyWithReload(() =>
  import('@/screens/Workspace/Workspace.Layout').then((m) => ({ default: m.WorkspaceLayout }))
);

const LazyWorkspaceFilesTable = lazyWithReload(() =>
  import('@/screens/Workspace.FilesTable').then((m) => ({ default: m.WorkspaceFilesTable }))
);

const LazyWorkspaceFolder = lazyWithReload(() =>
  import('@/screens/Workspace.Folder').then((m) => ({ default: m.WorkspaceFolder }))
);

const LazyFolderNotFound = lazyWithReload(() =>
  import('@/screens/Workspace.Folder/Folder.NotFound').then((m) => ({ default: m.default }))
);

const LazyFolderTabFiles = lazyWithReload(() =>
  import('@/screens/Workspace.Folder/Tab.Files').then((m) => ({ default: m.FolderTabFiles }))
);

const LazyFolderTabDetails = lazyWithReload(() =>
  import('@/screens/Workspace.Folder/Tab.Details').then((m) => ({ default: m.FolderTabDetails }))
);

const LazyWorkspaceFile = lazyWithReload(() =>
  import('@/screens/Workspace.File').then((m) => ({ default: m.WorkspaceFile }))
);

const LazyFileNotFound = lazyWithReload(() =>
  import('@/screens/Workspace.File').then((m) => ({ default: m.FileNotFound }))
);

const LazyFileTabPreview = lazyWithReload(() =>
  import('@/screens/Workspace.File/File.Tab.Preview').then((m) => ({ default: m.FileTabPreview }))
);

const LazyFileTabSummary = lazyWithReload(() =>
  import('@/screens/Workspace.File/File.Tab.Summary').then((m) => ({ default: m.FileTabSummary }))
);

const LazyFileTabDetails = lazyWithReload(() =>
  import('@/screens/Workspace.File/File.Tab.Details').then((m) => ({ default: m.FileTabDetails }))
);

const LazyWorkspaceBrand = lazyWithReload(() =>
  import('@/screens/Workspace.Brand').then((m) => ({ default: m.WorkspaceBrand }))
);

const LazyBrandNotFound = lazyWithReload(() =>
  import('@/screens/Workspace.Brand').then((m) => ({ default: m.BrandNotFound }))
);

const LazyBrandTabFiles = lazyWithReload(() =>
  import('@/screens/Workspace.Brand/Tab.Files').then((m) => ({ default: m.BrandTabFiles }))
);

const LazyBrandTabProjects = lazyWithReload(() =>
  import('@/screens/Workspace.Brand/Tab.Projects').then((m) => ({ default: m.BrandTabProjects }))
);

const LazyBrandTabProjectTracker = lazyWithReload(() =>
  import('@/screens/Workspace.Brand/Tab.ProjectTracker').then((m) => ({ default: m.BrandTabProjectTracker }))
);

const LazyWorkspaceTranscript = lazyWithReload(() =>
  import('@/screens/Workspace.Transcript/Workspace.Transcript').then((m) => ({
    default: m.WorkspaceTranscript,
  }))
);

// Workspace search
const LazyWorkspaceSearch = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch').then((m) => ({ default: m.Search }))
);

const LazySearchTabAll = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch/Search.Tab.All').then((m) => ({ default: m.default }))
);

const LazySearchTabBrands = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch/Search.Tab.Brands').then((m) => ({ default: m.default }))
);

const LazySearchTabDashboards = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch/Search.Tab.Dashboards').then((m) => ({ default: m.default }))
);

const LazySearchTabFiles = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch/Search.Tab.Files').then((m) => ({ default: m.default }))
);

const LazySearchTabFolders = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch/Search.Tab.Folders').then((m) => ({ default: m.default }))
);

const LazySearchTabProjects = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch/Search.Tab.Projects').then((m) => ({ default: m.default }))
);

const LazySearchTabQuantAnalyses = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch/Search.Tab.QuantAnalyses').then((m) => ({ default: m.default }))
);

const LazySearchTabTranscripts = lazyWithReload(() =>
  import('@/screens/WorkspaceGlobalSearch/Search.Tab.Transcripts').then((m) => ({ default: m.default }))
);

// Segmentation screens
const LazySegmentationScreen = lazyWithReload(() =>
  import('@/screens/Segmentation').then((m) => ({ default: m.SegmentationScreen }))
);

const LazySegmentProfileScreen = lazyWithReload(() =>
  import('@/screens/Segmentation').then((m) => ({ default: m.SegmentProfileScreen }))
);

const LazyExploreSegmentsPage = lazyWithReload(() =>
  import('@/screens/Segmentation').then((m) => ({ default: m.ExploreSegmentsPage }))
);

const LazyCrossSegmentDashboardScreen = lazyWithReload(() =>
  import('@/screens/Segmentation').then((m) => ({ default: m.CrossSegmentDashboardScreen }))
);

// Message screens
const LazyMessageLibraryScreen = lazyWithReload(() =>
  import('@/screens/MessageLibrary').then((m) => ({ default: m.MessageLibraryScreen }))
);

const LazyMessageGeneratorScreen = lazyWithReload(() =>
  import('@/screens/MessageGenerator').then((m) => ({ default: m.MessageGeneratorScreen }))
);

const LazyMessageTestingScreen = lazyWithReload(() =>
  import('@/screens/MessageTesting/MessageTestingScreen').then((m) => ({
    default: m.MessageTestingScreen,
  }))
);

// Settings
const LazySettingsScreen = lazyWithReload(() =>
  import('@/screens/Settings').then((m) => ({ default: m.SettingsScreen }))
);

const LazySettingsAccountScreen = lazyWithReload(() =>
  import('@/screens/Settings').then((m) => ({ default: m.SettingsAccountScreen }))
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

function buildRoutes() {
  const defaultPaths = getAppDefaultPaths();

  const mainRoute = {
    path: '/',
    element: (
      <RootVendors>
        <AppSubscriber>
          <ProvideDefaultPaths>
            <Layout />
          </ProvideDefaultPaths>
        </AppSubscriber>
      </RootVendors>
    ),
    children: [
      { index: true, element: <Navigate to="/home" /> },
      {
        path: '_/auth/oauth2',
        children: [
          { path: 'logout', element: <OAuth2Logout /> },
          { path: ':identifier', element: <OAuth2 /> },
        ],
      },
      {
        path: '_/auth/trinity-edge-callback',
        element: <TrinityEdgeAuthCallback />,
      },
      {
        path: '_/auth/salesforce-callback',
        element: <SalesforceAuthCallback />,
      },
      {
        path: defaultPaths.login.substring(1),
        element: <OAuth2Login />,
      },
      {
        path: 'user-disabled',
        element: <UserDisabled />,
      },
      {
        path: 'home',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Home />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'pages/:pageId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyPageScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'main',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyAnalystMain />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'calendar',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyCalendar />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'chat',
        children: [
          {
            path: ':chatIdentifier?',
            element: (
              <AuthenticatedGuard>
                <AppInitializedGuard>
                  <Suspense fallback={null}>
                    <LazyChatScreen />
                  </Suspense>
                </AppInitializedGuard>
              </AuthenticatedGuard>
            ),
          },
        ],
      },
      {
        path: 'diagnostics',
        element: (
          <Suspense fallback={null}>
            <LazyDiagnosticsScreen />
          </Suspense>
        ),
      },
      {
        path: defaultPaths.manualLogin.substring(1),
        element: (
          <BrandingInitializedGuard>
            <UnauthenticatedGuard>
              <LoginScreen />
            </UnauthenticatedGuard>
          </BrandingInitializedGuard>
        ),
      },
      {
        path: 'logout',
        element: <LogoutScreen />,
      },
      {
        path: 'projects/:projectId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyProjectMain />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          { index: true, element: <Navigate to="entities" replace /> },
          { path: 'entities', element: <Suspense fallback={null}><LazyProjectEntities /></Suspense> },
          { path: 'files', element: <Suspense fallback={null}><LazyProjectFiles /></Suspense> },
          { path: 'details', element: <Suspense fallback={null}><LazyProjectDetails /></Suspense> },
          { path: 'dashboard', element: <Suspense fallback={null}><LazyProjectDashboard /></Suspense> },
          { path: 'segments', element: <Suspense fallback={null}><LazyProjectSegmentsList /></Suspense> },
          { path: 'segments/:segmentId', element: <Suspense fallback={null}><LazyProjectSegmentProfile /></Suspense> },
          { path: 'quantanalysis', element: <Suspense fallback={null}><LazyQuantAnalysis /></Suspense> },
          { path: 'quantanalysis/:quantAnalysisId', element: <Suspense fallback={null}><LazyQuantAnalysis /></Suspense> },
          { path: 'quantanalysis/:quantAnalysisId/:versionNumber', element: <Suspense fallback={null}><LazyQuantAnalysis /></Suspense> },
          { path: 'tags/:tagId?', element: <Suspense fallback={null}><LazyProjectTags /></Suspense> },
          { path: 'patient-journey', element: <Suspense fallback={null}><LazyProjectPatientJourney /></Suspense> },
          { path: 'highlights', element: <Suspense fallback={null}><LazyProjectHighlights /></Suspense> },
          { path: 'summary', element: <Suspense fallback={null}><LazyProjectSummary /></Suspense> },
          { path: 'responses', element: <Suspense fallback={null}><LazyProjectResponses /></Suspense> },
          { path: 'chart-library', element: <Suspense fallback={null}><LazyProjectChartLibrary /></Suspense> },
        ],
      },
      // Standalone (not nested under Project.Main's tab shell) — the segment dashboard is a
      // full page with its own breadcrumbs, same as apps/frontend's SegmentDashboards screen,
      // not a project subtab.
      {
        path: 'projects/:projectId/segments/:segmentId/dashboard',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyProjectSegmentDashboard />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      // Standalone, same reasoning as the segment dashboard above — the "Chat with Digital
      // Twins" gallery/chat screen is a full page, not a project subtab.
      {
        path: 'projects/:projectId/hcp-digital-twins',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyProjectHCPDigitalTwins />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        // Standalone Patient Journey dashboard -- deliberately a sibling of
        // `projects/:projectId`, not one of its tab children, matching the legacy
        // apps/frontend standalone page (which also lives outside the project tabs).
        path: 'projects/:projectId/patient-journey/:journeyId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyPatientJourneyStandalonePage />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          { index: true, element: <Suspense fallback={null}><LazyProjectPatientJourneyDashboardContainer /></Suspense> },
          { path: 'stage/:stageId', element: <Suspense fallback={null}><LazyProjectPatientJourneyStageContainer /></Suspense> },
          { path: 'manage', element: <Suspense fallback={null}><LazyProjectPatientJourneyManageContainer /></Suspense> },
        ],
      },
      {
        path: 'project/:projectId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyProjectMain />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <Suspense fallback={null}><LazyProjectDashboard /></Suspense> },
        ],
      },
      {
        path: 'workspaces',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyWorkspaceLayout />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          {
            index: true,
            element: <Suspense fallback={null}><LazyWorkspaceFilesTable /></Suspense>,
          },
          {
            path: 'search',
            element: <Suspense fallback={null}><LazyWorkspaceSearch /></Suspense>,
            children: [
              { index: true, element: <RedirectToAllTab /> },
              { path: 'all', element: <Suspense fallback={null}><LazySearchTabAll /></Suspense> },
              { path: 'files', element: <Suspense fallback={null}><LazySearchTabFiles /></Suspense> },
              { path: 'folders', element: <Suspense fallback={null}><LazySearchTabFolders /></Suspense> },
              { path: 'brands', element: <Suspense fallback={null}><LazySearchTabBrands /></Suspense> },
              { path: 'dashboards', element: <Suspense fallback={null}><LazySearchTabDashboards /></Suspense> },
              { path: 'quantanalyses', element: <Suspense fallback={null}><LazySearchTabQuantAnalyses /></Suspense> },
              { path: 'projects', element: <Suspense fallback={null}><LazySearchTabProjects /></Suspense> },
              { path: 'transcripts', element: <Suspense fallback={null}><LazySearchTabTranscripts /></Suspense> },
            ],
          },
          {
            path: 'transcripts/:transcriptId',
            element: <Suspense fallback={null}><LazyWorkspaceTranscript /></Suspense>,
          },
          {
            path: 'folders/:folderId',
            element: <Suspense fallback={null}><LazyWorkspaceFolder /></Suspense>,
            children: [
              { index: true, element: <Navigate to="files" replace /> },
              { path: 'files', element: <Suspense fallback={null}><LazyFolderTabFiles /></Suspense> },
              { path: 'details', element: <Suspense fallback={null}><LazyFolderTabDetails /></Suspense> },
            ],
          },
          {
            path: 'folders/not-found',
            element: <Suspense fallback={null}><LazyFolderNotFound /></Suspense>,
          },
          {
            path: 'files/not-found',
            element: <Suspense fallback={null}><LazyFileNotFound /></Suspense>,
          },
          {
            path: 'files/:fileId',
            element: <Suspense fallback={null}><LazyWorkspaceFile /></Suspense>,
            children: [
              { index: true, element: <RedirectToFilePreview /> },
              { path: 'preview', element: <Suspense fallback={null}><LazyFileTabPreview /></Suspense> },
              { path: 'summary', element: <Suspense fallback={null}><LazyFileTabSummary /></Suspense> },
              { path: 'details', element: <Suspense fallback={null}><LazyFileTabDetails /></Suspense> },
            ],
          },
          {
            path: 'brands/:brandId',
            element: <Suspense fallback={null}><LazyWorkspaceBrand /></Suspense>,
            children: [
              { path: 'files', element: <Suspense fallback={null}><LazyBrandTabFiles /></Suspense> },
              { path: 'projects', element: <Suspense fallback={null}><LazyBrandTabProjects /></Suspense> },
              { path: 'tracker', element: <Suspense fallback={null}><LazyBrandTabProjectTracker /></Suspense> },
            ],
          },
          {
            path: 'brands/not-found',
            element: <Suspense fallback={null}><LazyBrandNotFound /></Suspense>,
          },
        ],
      },
      {
        path: 'segmentation',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazySegmentationScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'explore-segments',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyExploreSegmentsPage />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'segmentation/cross-segment-dashboard',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyCrossSegmentDashboardScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'segmentation/segment/:pageId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazySegmentProfileScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'message-library',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyMessageLibraryScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'message-generator/:messageGenSessionId?',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyMessageGeneratorScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'message-testing/:runId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazyMessageTestingScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      // NOTE: The global /segments, /segments/:id, and /segments/:id/manage
      // routes were removed in favor of the project subtab at
      // /projects/:projectId/segments (see Project.Segments.Container).
      // SegmentListScreen + SegmentManageScreen are still exported because
      // the project subtab reuses them. The segments_v2-backed SegmentDashboardScreen
      // (screens/Segments/SegmentDashboard.tsx) was deleted as dead code — the segment
      // dashboard is now served by the project-scoped
      // segments/:segmentId/dashboard route above (ProjectSegmentDashboard).
      {
        path: 'settings',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazySettingsScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          { index: true, element: <Navigate to="account" replace /> },
          { path: 'account', element: <Suspense fallback={null}><LazySettingsAccountScreen /></Suspense> },
        ],
      },
      {
        path: '*',
        element: <Navigate to={defaultPaths.home} replace />,
      },
    ],
  };

  const routes = [];

  if (isLowerEnv()) {
    routes.push({
      path: '/_iframe',
      element: <IFrameTest />,
    });
  }

  routes.push(mainRoute);
  return routes;
}

export function createRouter() {
  return sentryCreateBrowserRouter(buildRoutes());
}
